import { useEffect, useState } from 'react';

import { WINDOW_DATE_ID, isWindowSlice, type EventSliceSeed } from '@/lib/city-event-slices';
import { isSameDay } from '@todo/core/date-range';
import { DEFAULT_EVENT_FILTERS, eventFilterArgs } from '@todo/core/hub-filters';
import type { Locale } from '@todo/core/i18n/locales';
import {
  EVENTS_RPC,
  LIST_BUCKET,
  cityEventSliceSeedKey,
  cityEventsPageQuery,
  eventsRpcArgs,
} from '@todo/core/list-queries';
import { getPrefetched } from '@todo/core/static-prefetch';
import { supabase, type DbEvent } from '@todo/core/supabase';

export type EventSliceState = {
  seed: EventSliceSeed | null;
  loading: boolean;
  error: string | null;
};

type Fetched = { key: string; seed: EventSliceSeed | null; error: string | null };

/**
 * The rows behind a /[city]/events/[slice] page.
 *
 * The first render returns what the export baked (getPrefetched), so it
 * matches the HTML. A seed isn't refetched — it's no staler than the page
 * around it — except a window baked on an earlier Paris day (a deploy that
 * didn't run): the window has moved, so the browser asks
 * event_shows_filtered for today's. With no seed at all (a client-side
 * navigation, `expo start`, native), it fetches: a window from
 * event_shows_filtered, a category from event_shows.
 */
export function useEventSlice(cityId: string, slice: string, locale: Locale): EventSliceState {
  const key = `${cityId}:${slice}:${locale}`;
  const [baked] = useState(() =>
    getPrefetched<EventSliceSeed>(LIST_BUCKET, cityEventSliceSeedKey(cityId, slice, locale)),
  );
  const [fetched, setFetched] = useState<Fetched | null>(null);

  useEffect(() => {
    const window = isWindowSlice(slice);
    const stale = baked != null && window && !isSameDay(new Date(baked.builtAt), new Date());
    if (baked && !stale) return;

    let cancelled = false;
    const builtAt = new Date().toISOString();
    const done = (seed: EventSliceSeed | null, error: string | null) => {
      if (!cancelled) setFetched({ key, seed, error });
    };

    if (window) {
      const args = eventFilterArgs({ ...DEFAULT_EVENT_FILTERS, when: WINDOW_DATE_ID[slice] }, cityId, 730);
      supabase.rpc('event_shows_filtered', args).then(({ data, error }) =>
        done(
          error
            ? null
            : {
                rows: (data ?? []) as DbEvent[],
                from: args.p_from,
                until: args.p_until,
                builtAt,
                // A stale page's "coming up next" is still a fair offer.
                ...(baked?.next ? { next: baked.next } : {}),
              },
          error?.message ?? null,
        ),
      );
    } else {
      supabase
        .rpc(EVENTS_RPC, eventsRpcArgs({ ...cityEventsPageQuery(cityId), category: slice }))
        .then(({ data, error }) =>
          done(
            error ? null : { rows: (data ?? []) as DbEvent[], from: null, until: null, builtAt },
            error?.message ?? null,
          ),
        );
    }

    return () => {
      cancelled = true;
    };
  }, [baked, cityId, slice, key]);

  const current = fetched?.key === key ? fetched : null;
  // A failed refresh keeps the baked list on screen.
  const seed = current?.seed ?? baked ?? null;
  return { seed, loading: !seed && !current, error: current?.error ?? null };
}
